
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BarChart, Bar, XAxis, YAxis, PieChart, Pie, Cell, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DemographicData, DemographicItem, GradeItem } from "./types";

const DemographicFeedbackAnalytics = () => {
  const [activeTab, setActiveTab] = React.useState("districts");

  const { data: demographicData, isLoading } = useQuery({
    queryKey: ["demographicFeedbackAnalytics"],
    queryFn: async () => {
      // First get all feedback data
      const { data: registrationData, error: regError } = await supabase
        .from("course_registrations")
        .select(`
          id,
          overall_rating,
          teaching_grades,
          user_id
        `)
        .not("feedback", "is", null);

      if (regError) throw regError;
      
      // Then get all profiles data
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select(`
          id,
          school,
          school_district,
          position
        `);
        
      if (profilesError) throw profilesError;
      
      // Create a map of profile data for easier lookup
      const profileMap: Record<string, any> = {};
      profilesData.forEach(profile => {
        profileMap[profile.id] = profile;
      });
      
      // Process by school district
      const districtMap: Record<string, {
        name: string;
        feedbackCount: number;
        totalRating: number;
        participants: Set<string>;
      }> = {};
      
      const schoolMap: Record<string, {
        name: string;
        district: string;
        feedbackCount: number;
        totalRating: number;
        participants: Set<string>;
      }> = {};
      
      const positionMap: Record<string, {
        name: string;
        feedbackCount: number;
        totalRating: number;
        participants: Set<string>;
      }> = {};
      
      const gradeMap: Record<string, {
        name: string;
        feedbackCount: number;
        totalRating: number;
      }> = {};
      
      registrationData.forEach(item => {
        const profile = item.user_id ? profileMap[item.user_id] : null;
        const district = profile?.school_district || "Unknown";
        const school = profile?.school || "Unknown";
        const position = profile?.position || "Unknown";
        const grades = item.teaching_grades || [];
        
        // District processing
        if (!districtMap[district]) {
          districtMap[district] = {
            name: district,
            feedbackCount: 0,
            totalRating: 0,
            participants: new Set(),
          };
        }
        districtMap[district].feedbackCount++;
        if (item.overall_rating) {
          districtMap[district].totalRating += item.overall_rating;
        }
        if (item.user_id) {
          districtMap[district].participants.add(item.user_id);
        }
        
        // School processing
        if (!schoolMap[school]) {
          schoolMap[school] = {
            name: school,
            district: district,
            feedbackCount: 0,
            totalRating: 0,
            participants: new Set(),
          };
        }
        schoolMap[school].feedbackCount++;
        if (item.overall_rating) {
          schoolMap[school].totalRating += item.overall_rating;
        }
        if (item.user_id) {
          schoolMap[school].participants.add(item.user_id);
        }
        
        // Position processing
        if (!positionMap[position]) {
          positionMap[position] = {
            name: position,
            feedbackCount: 0,
            totalRating: 0,
            participants: new Set(),
          };
        }
        positionMap[position].feedbackCount++;
        if (item.overall_rating) {
          positionMap[position].totalRating += item.overall_rating;
        }
        if (item.user_id) {
          positionMap[position].participants.add(item.user_id);
        }
        
        // Grade level processing
        grades.forEach(grade => {
          if (!gradeMap[grade]) {
            gradeMap[grade] = {
              name: grade,
              feedbackCount: 0,
              totalRating: 0
            };
          }
          gradeMap[grade].feedbackCount++;
          if (item.overall_rating) {
            gradeMap[grade].totalRating += item.overall_rating;
          }
        });
      });
      
      // Calculate averages and format data
      const districts = Object.values(districtMap).map(district => ({
        ...district,
        avgRating: district.feedbackCount > 0 ? district.totalRating / district.feedbackCount : 0,
        participantCount: district.participants.size
      })) as DemographicItem[];
      
      const schools = Object.values(schoolMap).map(school => ({
        ...school,
        avgRating: school.feedbackCount > 0 ? school.totalRating / school.feedbackCount : 0,
        participantCount: school.participants.size
      })) as DemographicItem[];
      
      const positions = Object.values(positionMap).map(position => ({
        ...position,
        avgRating: position.feedbackCount > 0 ? position.totalRating / position.feedbackCount : 0,
        participantCount: position.participants.size
      })) as DemographicItem[];
      
      const grades = Object.values(gradeMap).map(grade => ({
        ...grade,
        avgRating: grade.feedbackCount > 0 ? grade.totalRating / grade.feedbackCount : 0
      })) as GradeItem[];
      
      return {
        districts,
        schools,
        positions,
        grades
      } as DemographicData;
    },
  });
  
  // Colors for charts
  const COLORS = ['#004161', '#cfb9a2', '#0f8f8c', '#b1c3cf', '#f1eceb', '#005b8c', '#0a7d7a', '#c0a68e'];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <p className="text-muted-foreground">Loading demographic analytics...</p>
      </div>
    );
  }

  // Sort districts by feedback count
  const sortedDistricts = demographicData?.districts
    .sort((a, b) => b.feedbackCount - a.feedbackCount)
    .slice(0, 10) || [];
    
  // Sort schools by feedback count
  const sortedSchools = demographicData?.schools
    .sort((a, b) => b.feedbackCount - a.feedbackCount)
    .slice(0, 10) || [];
    
  // Sort positions by feedback count
  const sortedPositions = demographicData?.positions
    .sort((a, b) => b.feedbackCount - a.feedbackCount)
    .slice(0, 10) || [];
    
  // Sort grades by feedback count
  const sortedGrades = demographicData?.grades
    .sort((a, b) => b.feedbackCount - a.feedbackCount) || [];

  return (
    <div className="space-y-6">
      <Tabs defaultValue={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="districts" className="font-section">School Districts</TabsTrigger>
          <TabsTrigger value="schools" className="font-section">Schools</TabsTrigger>
          <TabsTrigger value="positions" className="font-section">Positions</TabsTrigger>
          <TabsTrigger value="grades" className="font-section">Grade Levels</TabsTrigger>
        </TabsList>
        
        <TabsContent value="districts" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-primary">Feedback by School District</CardTitle>
                <CardDescription className="font-subtitle">
                  Number of feedback submissions by district
                </CardDescription>
              </CardHeader>
              <CardContent className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={sortedDistricts.map(d => ({
                      name: d.name,
                      feedback: d.feedbackCount
                    }))}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      tick={{ fontSize: 12 }}
                      width={80}
                    />
                    <Tooltip />
                    <Bar dataKey="feedback" fill="#004161">
                      {sortedDistricts.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-primary">District Participant Count</CardTitle>
                <CardDescription className="font-subtitle">
                  Number of unique participants by district
                </CardDescription>
              </CardHeader>
              <CardContent className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sortedDistricts.map(d => ({
                        name: d.name,
                        value: d.participantCount
                      }))}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name.length > 15 ? name.substring(0, 15) + '...' : name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {sortedDistricts.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value} participants`, '']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-primary">School District Detail</CardTitle>
              <CardDescription className="font-subtitle">
                Feedback metrics by school district
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 font-section">District</th>
                      <th className="text-left p-2 font-section">Participants</th>
                      <th className="text-left p-2 font-section">Feedback Count</th>
                      <th className="text-left p-2 font-section">Avg. Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {demographicData?.districts
                      .sort((a, b) => b.feedbackCount - a.feedbackCount)
                      .map(district => (
                        <tr key={district.name} className="border-b hover:bg-muted/50">
                          <td className="p-2 font-body">{district.name}</td>
                          <td className="p-2 font-body">{district.participantCount}</td>
                          <td className="p-2 font-body">{district.feedbackCount}</td>
                          <td className="p-2 font-body">
                            {district.feedbackCount > 0 ? 
                              <div className="flex items-center">
                                <span className="mr-2">{district.avgRating.toFixed(1)}/4</span>
                                <div className="flex">
                                  {[...Array(4)].map((_, i) => (
                                    <span 
                                      key={i} 
                                      className={`text-xs ${i < Math.round(district.avgRating) ? 'text-amber-400' : 'text-gray-300'}`}
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                              </div> : 
                              'No ratings'
                            }
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="schools" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-primary">Feedback by School</CardTitle>
                <CardDescription className="font-subtitle">
                  Number of feedback submissions by school
                </CardDescription>
              </CardHeader>
              <CardContent className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={sortedSchools.map(s => ({
                      name: s.name,
                      feedback: s.feedbackCount
                    }))}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      tick={{ fontSize: 12 }}
                      width={80}
                    />
                    <Tooltip />
                    <Bar dataKey="feedback" fill="#0f8f8c">
                      {sortedSchools.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-primary">Top Schools by Rating</CardTitle>
                <CardDescription className="font-subtitle">
                  Schools with highest average ratings
                </CardDescription>
              </CardHeader>
              <CardContent className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={demographicData?.schools
                      .filter(s => s.feedbackCount >= 3)
                      .sort((a, b) => b.avgRating - a.avgRating)
                      .slice(0, 10)
                      .map(s => ({
                        name: s.name,
                        rating: parseFloat(s.avgRating.toFixed(1))
                      }))}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 4]} />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      tick={{ fontSize: 12 }}
                      width={80}
                    />
                    <Tooltip formatter={(value) => [`${value}/4`, 'Average Rating']} />
                    <Bar dataKey="rating" fill="#cfb9a2">
                      {demographicData?.schools
                        .filter(s => s.feedbackCount >= 3)
                        .sort((a, b) => b.avgRating - a.avgRating)
                        .slice(0, 10)
                        .map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-primary">School Detail</CardTitle>
              <CardDescription className="font-subtitle">
                Feedback metrics by school
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 font-section">School</th>
                      <th className="text-left p-2 font-section">District</th>
                      <th className="text-left p-2 font-section">Participants</th>
                      <th className="text-left p-2 font-section">Feedback Count</th>
                      <th className="text-left p-2 font-section">Avg. Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {demographicData?.schools
                      .sort((a, b) => b.feedbackCount - a.feedbackCount)
                      .map(school => (
                        <tr key={school.name} className="border-b hover:bg-muted/50">
                          <td className="p-2 font-body">{school.name}</td>
                          <td className="p-2 font-body">{school.district}</td>
                          <td className="p-2 font-body">{school.participantCount}</td>
                          <td className="p-2 font-body">{school.feedbackCount}</td>
                          <td className="p-2 font-body">
                            {school.feedbackCount > 0 ? 
                              <div className="flex items-center">
                                <span className="mr-2">{school.avgRating.toFixed(1)}/4</span>
                                <div className="flex">
                                  {[...Array(4)].map((_, i) => (
                                    <span 
                                      key={i} 
                                      className={`text-xs ${i < Math.round(school.avgRating) ? 'text-amber-400' : 'text-gray-300'}`}
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                              </div> : 
                              'No ratings'
                            }
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="positions" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-primary">Feedback by Position</CardTitle>
                <CardDescription className="font-subtitle">
                  Number of feedback submissions by position
                </CardDescription>
              </CardHeader>
              <CardContent className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sortedPositions.map(p => ({
                        name: p.name,
                        value: p.feedbackCount
                      }))}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name.length > 15 ? name.substring(0, 15) + '...' : name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {sortedPositions.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value} feedback submissions`, '']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-primary">Position Ratings</CardTitle>
                <CardDescription className="font-subtitle">
                  Average ratings by position
                </CardDescription>
              </CardHeader>
              <CardContent className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={sortedPositions
                      .filter(p => p.feedbackCount >= 3)
                      .map(p => ({
                        name: p.name,
                        rating: parseFloat(p.avgRating.toFixed(1))
                      }))}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 4]} />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      tick={{ fontSize: 12 }}
                      width={80}
                    />
                    <Tooltip formatter={(value) => [`${value}/4`, 'Average Rating']} />
                    <Bar dataKey="rating" fill="#b1c3cf">
                      {sortedPositions
                        .filter(p => p.feedbackCount >= 3)
                        .map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-primary">Position Detail</CardTitle>
              <CardDescription className="font-subtitle">
                Feedback metrics by position
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 font-section">Position</th>
                      <th className="text-left p-2 font-section">Participants</th>
                      <th className="text-left p-2 font-section">Feedback Count</th>
                      <th className="text-left p-2 font-section">Avg. Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {demographicData?.positions
                      .sort((a, b) => b.feedbackCount - a.feedbackCount)
                      .map(position => (
                        <tr key={position.name} className="border-b hover:bg-muted/50">
                          <td className="p-2 font-body">{position.name}</td>
                          <td className="p-2 font-body">{position.participantCount}</td>
                          <td className="p-2 font-body">{position.feedbackCount}</td>
                          <td className="p-2 font-body">
                            {position.feedbackCount > 0 ? 
                              <div className="flex items-center">
                                <span className="mr-2">{position.avgRating.toFixed(1)}/4</span>
                                <div className="flex">
                                  {[...Array(4)].map((_, i) => (
                                    <span 
                                      key={i} 
                                      className={`text-xs ${i < Math.round(position.avgRating) ? 'text-amber-400' : 'text-gray-300'}`}
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                              </div> : 
                              'No ratings'
                            }
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="grades" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-primary">Feedback by Grade Level</CardTitle>
                <CardDescription className="font-subtitle">
                  Number of feedback submissions by teaching grade
                </CardDescription>
              </CardHeader>
              <CardContent className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={sortedGrades.map(g => ({
                      name: g.name,
                      feedback: g.feedbackCount
                    }))}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      tick={{ fontSize: 12 }}
                      width={80}
                    />
                    <Tooltip />
                    <Bar dataKey="feedback" fill="#0f8f8c">
                      {sortedGrades.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-primary">Grade Level Ratings</CardTitle>
                <CardDescription className="font-subtitle">
                  Average ratings by teaching grade
                </CardDescription>
              </CardHeader>
              <CardContent className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={sortedGrades
                      .filter(g => g.feedbackCount >= 3)
                      .map(g => ({
                        name: g.name,
                        rating: parseFloat(g.avgRating.toFixed(1))
                      }))}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 4]} />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      tick={{ fontSize: 12 }}
                      width={80}
                    />
                    <Tooltip formatter={(value) => [`${value}/4`, 'Average Rating']} />
                    <Bar dataKey="rating" fill="#f1eceb">
                      {sortedGrades
                        .filter(g => g.feedbackCount >= 3)
                        .map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-primary">Grade Level Detail</CardTitle>
              <CardDescription className="font-subtitle">
                Feedback metrics by teaching grade
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 font-section">Grade Level</th>
                      <th className="text-left p-2 font-section">Feedback Count</th>
                      <th className="text-left p-2 font-section">Avg. Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {demographicData?.grades
                      .sort((a, b) => b.feedbackCount - a.feedbackCount)
                      .map(grade => (
                        <tr key={grade.name} className="border-b hover:bg-muted/50">
                          <td className="p-2 font-body">{grade.name}</td>
                          <td className="p-2 font-body">{grade.feedbackCount}</td>
                          <td className="p-2 font-body">
                            {grade.feedbackCount > 0 ? 
                              <div className="flex items-center">
                                <span className="mr-2">{grade.avgRating.toFixed(1)}/4</span>
                                <div className="flex">
                                  {[...Array(4)].map((_, i) => (
                                    <span 
                                      key={i} 
                                      className={`text-xs ${i < Math.round(grade.avgRating) ? 'text-amber-400' : 'text-gray-300'}`}
                                    >
                                      ★
                                    </span>
                                  ))}
                                </div>
                              </div> : 
                              'No ratings'
                            }
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DemographicFeedbackAnalytics;
